import type {
  BlobResponse,
  SaveInvoiceRequest,
  SaveInvoiceResponse,
  ListInvoicesResponse,
  InvoiceDetailResponse,
  DeleteInvoiceRequest,
  DeleteInvoiceResponse,
  DeleteInvoiceItemsRequest,
  DeleteInvoiceItemsResponse,
  CashEntryRequest,
  CashEntryResponse,
  CashEntryBalanceRequest,
  CashEntryBalanceResponse,
} from "../types/billing";
import { getAuthHeaders, handleAuthError } from "./auth-api";
import { formatApiError } from "./error-message";

let baseUrl = "";
const API_PREFIX = "/api/v1";

export function setBillingBaseUrl(url: string): void {
  baseUrl = url.replace(/\/$/, "");
}

export function getBillingBaseUrl(): string {
  return `${baseUrl}${API_PREFIX}`;
}

export async function fetchBilling<T>(endpoint: string): Promise<T> {
  const url = `${baseUrl}${API_PREFIX}${endpoint}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    if (response.status === 401) handleAuthError();
    const errorBody = await response.json().catch(() => null);
    throw new Error(
      formatApiError(errorBody, response.status, response.statusText),
    );
  }

  return response.json() as Promise<T>;
}

export async function postBilling<T>(
  endpoint: string,
  data: Record<string, unknown>,
): Promise<T> {
  const url = `${baseUrl}${API_PREFIX}${endpoint}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    if (response.status === 401) {
      handleAuthError();
    }
    const errorBody = await response.json().catch(() => null);
    throw new Error(
      formatApiError(errorBody, response.status, response.statusText),
    );
  }

  return response.json() as Promise<T>;
}

export async function patchBilling<T>(
  endpoint: string,
  data: Record<string, unknown>,
): Promise<T> {
  const url = `${baseUrl}${API_PREFIX}${endpoint}`;

  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    if (response.status === 401) handleAuthError();
    const errorBody = await response.json().catch(() => null);
    throw new Error(
      formatApiError(errorBody, response.status, response.statusText),
    );
  }

  return response.json() as Promise<T>;
}

export async function deleteBilling<T>(endpoint: string): Promise<T> {
  const url = `${baseUrl}${API_PREFIX}${endpoint}`;

  const response = await fetch(url, {
    method: "DELETE",
    headers: { ...getAuthHeaders() },
  });

  if (!response.ok) {
    if (response.status === 401) handleAuthError();
    const errorBody = await response.json().catch(() => null);
    throw new Error(
      formatApiError(errorBody, response.status, response.statusText),
    );
  }

  return response.json() as Promise<T>;
}

export async function getClientBlob(
  shopId: string,
  type?: string,
): Promise<BlobResponse> {
  return postBilling<BlobResponse>("/billing/client-blob", {
    shopId,
    type: type ?? "all",
  });
}

export async function saveInvoice(
  request: SaveInvoiceRequest,
): Promise<SaveInvoiceResponse> {
  return postBilling<SaveInvoiceResponse>(
    "/billing/invoice",
    request as unknown as Record<string, unknown>,
  );
}

export async function listInvoices(
  shopId: string,
  fromDate: string,
  toDate: string,
): Promise<ListInvoicesResponse> {
  const qs = new URLSearchParams({ fromDate, toDate }).toString();
  return fetchBilling<ListInvoicesResponse>(
    `/billing/invoices/${encodeURIComponent(shopId)}?${qs}`,
  );
}

export async function getInvoiceDetail(
  shopId: string,
  documentId: string,
): Promise<InvoiceDetailResponse> {
  // documentId values contain slashes (e.g. GBR/26-27/508) — encode it
  // so it stays within a single path segment.
  return fetchBilling<InvoiceDetailResponse>(
    `/billing/invoices/${encodeURIComponent(shopId)}/${encodeURIComponent(documentId)}`,
  );
}

export async function deleteInvoice(
  request: DeleteInvoiceRequest,
): Promise<DeleteInvoiceResponse> {
  return postBilling<DeleteInvoiceResponse>(
    "/billing/invoice/cancel",
    request as unknown as Record<string, unknown>,
  );
}

export async function deleteInvoiceItems(
  request: DeleteInvoiceItemsRequest,
): Promise<DeleteInvoiceItemsResponse> {
  return postBilling<DeleteInvoiceItemsResponse>(
    "/billing/invoice/item/delete",
    request as unknown as Record<string, unknown>,
  );
}

export async function insertCashEntry(
  request: CashEntryRequest,
): Promise<CashEntryResponse> {
  return postBilling<CashEntryResponse>(
    "/billing/cash-entry",
    request as unknown as Record<string, unknown>,
  );
}

export async function getCashEntryBalance(
  request: CashEntryBalanceRequest,
): Promise<CashEntryBalanceResponse> {
  return postBilling<CashEntryBalanceResponse>(
    "/billing/cash-balance",
    request as unknown as Record<string, unknown>,
  );
}
